import { useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "../scripts/AuthContext";
import { API_BASE_URL } from "@/lib/apiBase";
import { fetchUserMe, seedUserMe } from "@/lib/userMeCache";
import {
  AUTO_AWAY_ACTIVITY_THROTTLE_MS,
  AUTO_AWAY_IDLE_MS,
  clearAutoAwayActive,
  isAutoAwayActive,
  setAutoAwayActive,
} from "@/lib/autoAwayPresence";

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "wheel",
];

/**
 * After 5m with no input activity while Active → Away (auto).
 * On activity / tab focus while auto-Away → Active.
 * Never changes Busy / DND / manual Away.
 */
export function useAutoAwayPresence() {
  const { accessToken } = useAuth();
  const statusRef = useRef("active");
  const idleTimerRef = useRef(null);
  const lastActivityPulseRef = useRef(0);
  const patchInFlightRef = useRef(false);

  useEffect(() => {
    if (!accessToken) return undefined;

    let cancelled = false;

    const applyLocalStatus = (presence_status) => {
      statusRef.current = presence_status;
      window.dispatchEvent(
        new CustomEvent("presence-changed", { detail: presence_status })
      );
    };

    const patchPresence = async (presence_status, { autoAway }) => {
      if (!accessToken || cancelled) return;
      if (statusRef.current === presence_status) {
        if (autoAway != null) setAutoAwayActive(autoAway);
        return;
      }
      if (patchInFlightRef.current) return;
      patchInFlightRef.current = true;
      try {
        const res = await axios.patch(
          `${API_BASE_URL}/user/me`,
          { presence_status },
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (cancelled) return;
        seedUserMe(accessToken, res.data);
        if (autoAway != null) setAutoAwayActive(autoAway);
        applyLocalStatus(presence_status);
      } catch (err) {
        console.error("Failed to auto-update presence:", err);
      } finally {
        patchInFlightRef.current = false;
      }
    };

    const clearIdleTimer = () => {
      if (idleTimerRef.current != null) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };

    const scheduleIdleTimer = () => {
      clearIdleTimer();
      idleTimerRef.current = setTimeout(() => {
        idleTimerRef.current = null;
        if (statusRef.current !== "active") return;
        void patchPresence("away", { autoAway: true });
      }, AUTO_AWAY_IDLE_MS);
    };

    const resumeIfAutoAway = () => {
      if (isAutoAwayActive() && statusRef.current === "away") {
        void patchPresence("active", { autoAway: false });
        return true;
      }
      return false;
    };

    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivityPulseRef.current < AUTO_AWAY_ACTIVITY_THROTTLE_MS) {
        return;
      }
      lastActivityPulseRef.current = now;

      resumeIfAutoAway();

      // Only schedule idle countdown while Available; Busy/DND/manual Away idle ignored.
      if (statusRef.current === "active" || isAutoAwayActive()) {
        scheduleIdleTimer();
      } else {
        clearIdleTimer();
      }
    };

    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      if (resumeIfAutoAway()) {
        scheduleIdleTimer();
        return;
      }
      if (statusRef.current === "active") {
        scheduleIdleTimer();
      }
    };

    const onPresenceChanged = (e) => {
      const next = e?.detail;
      if (typeof next !== "string" || !next) return;
      statusRef.current = next;
      if (next === "active") {
        clearAutoAwayActive();
        scheduleIdleTimer();
      } else if (next === "away" && isAutoAwayActive()) {
        // Keep auto-away flag; idle timer off until resume.
        clearIdleTimer();
      } else {
        // Busy / DND / manual Away
        clearAutoAwayActive();
        clearIdleTimer();
      }
    };

    (async () => {
      try {
        const me = await fetchUserMe(accessToken);
        if (cancelled) return;
        const status = me?.presence_status || "active";
        statusRef.current = status;
        if (status === "active") {
          clearAutoAwayActive();
          scheduleIdleTimer();
        } else if (status === "away" && isAutoAwayActive()) {
          // Persisted auto-away (reload / new tab): resume on next activity/focus.
          clearIdleTimer();
        } else {
          clearAutoAwayActive();
          clearIdleTimer();
        }
      } catch {
        if (!cancelled) {
          statusRef.current = "active";
          clearAutoAwayActive();
          scheduleIdleTimer();
        }
      }
    })();

    const opts = { capture: true, passive: true };
    for (const type of ACTIVITY_EVENTS) {
      window.addEventListener(type, onActivity, opts);
    }
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("presence-changed", onPresenceChanged);

    return () => {
      cancelled = true;
      clearIdleTimer();
      for (const type of ACTIVITY_EVENTS) {
        window.removeEventListener(type, onActivity, opts);
      }
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("presence-changed", onPresenceChanged);
    };
  }, [accessToken]);
}

/** Mount once under authenticated providers. */
export default function AutoAwayPresenceHost() {
  useAutoAwayPresence();
  return null;
}
